import { useAudioPlayerStore } from '@hts/shared/libs/zustand';
import { isNumber } from '@hts/shared/utils';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { HtsTypography } from '../HtsTypography';
import { AudioPlayerUI } from './AudioPlayerUI';

interface HtsAudioPlayerProps {
  src: string;
  songId: number;
  fullWidth?: boolean;
  showDuration?: boolean;
  showSongTitle?: boolean;
  songTitle?: string;
  titleTypographyProps?: React.ComponentProps<typeof HtsTypography>;
  playButtonClassName?: string;
  sliderContentContainerClassName?: string;
  containerClassName?: string;
  rightContent?: React.ReactNode;
  onPlay?: () => void;
}

export const HtsAudioPlayer: React.FC<HtsAudioPlayerProps> = memo(
  ({
    src,
    songId,
    fullWidth,
    showDuration = true,
    showSongTitle,
    songTitle,
    titleTypographyProps,
    playButtonClassName = '',
    containerClassName = '',
    sliderContentContainerClassName = '',
    rightContent,
    onPlay,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const {
      activeSongId,
      tracks,
      setActiveSong,
      updateTrackTime,
      updateTrackDuration,
      setTrackVisibility,
      audioElement,
    } = useAudioPlayerStore();

    const isActive = activeSongId === songId;
    const track = tracks.get(songId);
    const duration = track?.duration ?? 0;
    const currentTime = track?.currentTime ?? 0;
    const isPlaying = isActive && audioElement?.paused === false;

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setTrackVisibility(songId, entry.isIntersecting);

          if (!entry.isIntersecting && isActive) {
            setActiveSong(undefined);
          }
        },
        { threshold: 0.1 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, [songId, isActive]);

    useEffect(() => {
      if (!audioElement) return;

      const handlePlay = () => {
        if (isActive) {
          onPlay?.();
        }
      };

      const handleTimeUpdate = () => {
        if (isActive) {
          const time = audioElement.currentTime;
          updateTrackTime(songId, time);
        }
      };

      const handleLoadedMetadata = () => {
        updateTrackDuration(songId, audioElement.duration);
      };

      audioElement.addEventListener('play', handlePlay);
      audioElement.addEventListener('timeupdate', handleTimeUpdate);
      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        audioElement.removeEventListener('play', handlePlay);
        audioElement.removeEventListener('timeupdate', handleTimeUpdate);
        audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }, [songId, updateTrackTime, updateTrackDuration, audioElement, isActive, onPlay]);

    useEffect(() => {
      if (!audioElement) return;

      if (isActive) {
        if (audioElement.src !== src) {
          audioElement.src = src;

          if (track) {
            audioElement.currentTime = track.currentTime;
          }
        }

        audioElement.play().catch(console.error);
      }
    }, [isActive, audioElement, src, track]);

    const togglePlay = useCallback(() => {
      if (isPlaying) {
        setActiveSong(undefined);
      } else {
        setActiveSong(songId);
      }
    }, [isPlaying, setActiveSong, songId]);

    const changeCurrentTime = useCallback(
      (value: number | number[]) => {
        if (isNumber(value)) {
          if (isActive && audioElement) {
            audioElement.currentTime = value;
          }

          updateTrackTime(songId, value);
        }
      },
      [songId, updateTrackTime, isActive]
    );

    return (
      <div ref={containerRef}>
        <AudioPlayerUI
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          showSongTitle={showSongTitle}
          songTitle={songTitle}
          showDuration={showDuration}
          titleTypographyProps={titleTypographyProps}
          playButtonClassName={playButtonClassName}
          sliderContentContainerClassName={sliderContentContainerClassName}
          containerClassName={containerClassName}
          rightContent={rightContent}
          fullWidth={fullWidth}
          changeCurrentTime={changeCurrentTime}
          togglePlay={togglePlay}
        />
      </div>
    );
  }
);
